import { safeJsonParse } from "../stringUtility";

/*-----------------------FOR_CUSTOMER_PAGE-------------------------------*/

export function generateUniqueValuesForCustomerPage(keys, opts = {}) {
  const { caseInsensitive = true } = opts;
  if (!Array.isArray(keys)) return [];

  const norm = (v) => {
    if (v === null || v === undefined) return "";
    try {
      const s = String(v).trim();
      return caseInsensitive ? s.toLowerCase() : s;
    } catch {
      return "";
    }
  };

  const safeParse = (s) => {
    if (typeof s !== "string") return null;
    try {
      return JSON.parse(s);
    } catch {
      return null;
    }
  };

  const seen = new Set();
  const out = [];

  const addIfNewPrimitive = (uniqueKey, original) => {
    const k = norm(uniqueKey);
    if (!k) return false;
    if (!seen.has(k)) {
      seen.add(k);
      out.push(original);
      return true;
    }
    return false;
  };

  const addIfNewObject = (uniqueKey, obj) => {
    const k = norm(uniqueKey);
    if (!k) return false;
    if (!seen.has(k)) {
      seen.add(k);
      out.push(obj);
      return true;
    }
    return false;
  };

  for (const entry of keys) {
    if (entry === null || entry === undefined) continue;

    // --- Case: primitive (string/number/boolean) ---
    if (typeof entry === "string" || typeof entry === "number" || typeof entry === "boolean") {
      // If it's a string, try parsing JSON to check if it's a stringified object/array
      if (typeof entry === "string") {
        const parsedTop = safeParse(entry);

        // CASE 4: stringified array of objects -> parse and push inner parsed objects,
        // uniqueness based on inner.value
        if (Array.isArray(parsedTop)) {
          for (const inner of parsedTop) {
            if (!inner || typeof inner !== "object") continue;
            if (inner.value !== undefined && inner.value !== null) {
              addIfNewObject(inner.value, inner); // push parsed inner object
              continue;
            }
            // fallback: if inner.id present
            if (inner.id !== undefined && inner.id !== null) {
              addIfNewObject(inner.id, inner);
              continue;
            }
            // fallback: stringify inner
            try {
              addIfNewObject(JSON.stringify(inner), inner);
            } catch {}
          }
          continue; // processed this original string
        }

        // CASE 2: stringified object -> parse and use parsed.value for uniqueness, but push ORIGINAL string
        if (parsedTop && typeof parsedTop === "object" && !Array.isArray(parsedTop)) {
          if (parsedTop.value !== undefined && parsedTop.value !== null) {
            addIfNewPrimitive(parsedTop.value, entry); // push original string
            continue;
          }
          if (parsedTop.id !== undefined && parsedTop.id !== null) {
            addIfNewPrimitive(parsedTop.id, entry);
            continue;
          }
          // fallback: use serialized parsed as unique key but still push original string
          try {
            addIfNewPrimitive(JSON.stringify(parsedTop), entry);
          } catch {}
          continue;
        }
        // else parsedTop is null or primitive -> fallthrough to treat entry as plain primitive
      }

      // Plain primitive (or parsed primitive) -> uniqueness by primitive; push primitive as-is
      addIfNewPrimitive(entry, entry);
      continue;
    }

    // --- Case: native array of objects (Case 3) ---
    if (Array.isArray(entry)) {
      for (const inner of entry) {
        if (!inner || typeof inner !== "object") continue;
        // uniqueness by inner.id (preferred), fallback to inner.value
        if (inner.id !== undefined && inner.id !== null) {
          addIfNewObject(inner.id, inner);
          continue;
        }
        if (inner.value !== undefined && inner.value !== null) {
          addIfNewObject(inner.value, inner);
          continue;
        }
        try {
          addIfNewObject(JSON.stringify(inner), inner);
        } catch {}
      }
      continue;
    }

    // --- Case: native object (Case 3 variant) ---
    if (typeof entry === "object") {
      // Use id for uniqueness and push the object
      if (entry.id !== undefined && entry.id !== null) {
        addIfNewObject(entry.id, entry);
        continue;
      }

      // If it has a .value primitive, use that for uniqueness but still push the original object
      if (Object.prototype.hasOwnProperty.call(entry, "value")) {
        const v = entry.value;
        if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") {
          addIfNewPrimitive(v, entry); // push original object (we use addIfNewPrimitive because output can be mixed)
          continue;
        }

        // if value is array of objects: dedupe by inner.value but push the original object
        if (Array.isArray(v)) {
          for (const inner of v) {
            if (!inner) continue;
            if (inner.value !== undefined && inner.value !== null) {
              addIfNewPrimitive(inner.value, entry);
              continue;
            }
            if (inner.id !== undefined && inner.id !== null) {
              addIfNewPrimitive(inner.id, entry);
              continue;
            }
            try {
              addIfNewPrimitive(JSON.stringify(inner), entry);
            } catch {}
          }
          continue;
        }

        // if value is an object: prefer value.value then value.id
        if (v && typeof v === "object") {
          if (v.value !== undefined && v.value !== null) {
            addIfNewPrimitive(v.value, entry);
            continue;
          }
          if (v.id !== undefined && v.id !== null) {
            addIfNewPrimitive(v.id, entry);
            continue;
          }
          try {
            addIfNewPrimitive(JSON.stringify(v), entry);
          } catch {}
          continue;
        }
      }

      // fallback: try to find a primitive prop to use as uniqueness key, push original object
      const primitiveKey = Object.keys(entry).find((k) => {
        const vv = entry[k];
        return typeof vv === "string" || typeof vv === "number" || typeof vv === "boolean";
      });
      if (primitiveKey) {
        addIfNewPrimitive(entry[primitiveKey], entry);
        continue;
      }

      // final fallback: stringify whole object as uniqueness key and push original object
      try {
        addIfNewPrimitive(JSON.stringify(entry), entry);
      } catch {}
      continue;
    }

    // any other type => stringify and push if new
    try {
      addIfNewPrimitive(String(entry), entry);
    } catch {}
  }

  return out;
}

export function buildOptionsFromListCustomerPage(list, keys = "value") {
  if (!Array.isArray(list)) return [];

  const preferred = Array.isArray(keys) ? keys : [keys];

  const isPrimitive = (v) =>
    v === null || v === undefined ? false : ["string", "number", "boolean"].includes(typeof v);

  const tryParse = (s) => {
    if (typeof s !== "string") return null;
    try {
      return JSON.parse(s);
    } catch {
      return null;
    }
  };

  const getByPath = (obj, path) => {
    if (obj == null) return undefined;
    const parts = String(path).split(".");
    let cur = obj;
    for (const p of parts) {
      if (cur == null || typeof cur !== "object" || !Object.prototype.hasOwnProperty.call(cur, p)) return undefined;
      cur = cur[p];
    }
    return cur;
  };

  const pickPrimitiveFromObject = (obj) => {
    if (!obj || typeof obj !== "object") return null;
    const fields = ["value", "textForUrl", "label", "email", "name", "url", "id"];
    for (const f of fields) {
      if (Object.prototype.hasOwnProperty.call(obj, f) && isPrimitive(obj[f])) return String(obj[f]);
    }
    // one-level deeper: if obj.value is object try its fields
    if (obj.value && typeof obj.value === "object") {
      for (const f of fields) {
        if (Object.prototype.hasOwnProperty.call(obj.value, f) && isPrimitive(obj.value[f]))
          return String(obj.value[f]);
      }
    }
    return null;
  };

  return list.map((entry) => {
    try {
      // 1) primitives (string/number/boolean)
      if (isPrimitive(entry)) {
        // If it's a string that may be JSON (stringified object or array), try parse for label extraction.
        if (typeof entry === "string") {
          const parsed = tryParse(entry);
          if (parsed && typeof parsed === "object") {
            // If parsed is array -> try first inner object's common fields
            if (Array.isArray(parsed) && parsed.length > 0) {
              const first = parsed[0];
              const innerPick = pickPrimitiveFromObject(first);
              if (innerPick) return { label: innerPick, value: entry };
            } else {
              const pick = pickPrimitiveFromObject(parsed);
              if (pick) return { label: pick, value: entry };
            }
          }
        }
        return { label: String(entry), value: entry };
      }

      // 2) objects
      if (typeof entry === "object" && entry !== null) {
        // Try preferred keys first (supports nested like "value.textForUrl")
        for (const k of preferred) {
          if (!k) continue;
          const candidate = getByPath(entry, k);
          if (candidate === undefined || candidate === null) continue;

          if (isPrimitive(candidate)) return { label: String(candidate), value: entry };

          if (typeof candidate === "object") {
            const inner = pickPrimitiveFromObject(candidate);
            if (inner) return { label: inner, value: entry };
            // last resort: try first primitive prop on candidate
            for (const pk of Object.keys(candidate)) {
              const v = candidate[pk];
              if (isPrimitive(v)) return { label: String(v), value: entry };
            }
            continue;
          }

          // other types -> string
          return { label: String(candidate), value: entry };
        }

        // Preferred keys didn't find a label -> general fallbacks
        const pickTop = pickPrimitiveFromObject(entry);
        if (pickTop) return { label: pickTop, value: entry };

        // If entry.value is array -> use first inner object's primitive fields
        if (Array.isArray(entry.value) && entry.value.length > 0) {
          const first = entry.value[0];
          const firstPick = pickPrimitiveFromObject(first) ?? (isPrimitive(first) ? String(first) : null);
          if (firstPick) return { label: firstPick, value: entry };
        }

        // id -> value -> label -> email -> name -> url fallbacks handled in pickPrimitiveFromObject above.
        // As last resort choose the first primitive property on the entry
        for (const k of Object.keys(entry)) {
          const v = entry[k];
          if (isPrimitive(v)) return { label: String(v), value: entry };
        }

        // final fallback: short JSON preview so label isn't a huge blob
        try {
          const s = JSON.stringify(entry);
          const short = s.length > 80 ? s.slice(0, 77) + "..." : s;
          return { label: short, value: entry };
        } catch {
          return { label: String(entry), value: entry };
        }
      }

      // fallback for unexpected types
      return { label: String(entry), value: entry };
    } catch (err) {
      // silent fallback — preserve original behaviour
      return { label: String(entry), value: entry };
    }
  });
}

export function buildOptionsFromListCustomer(list, keys = "value", opts = {}) {
  if (!Array.isArray(list)) return [];

  const { unwrapNestedValue = false } = opts;
  const passed = Array.isArray(keys) ? [...keys] : [keys];
  // ensure "value" is always included (but don't duplicate)
  if (!passed.includes("value")) passed.push("value");

  return list.map((entry) => {
    try {
      const parsed = safeJsonParse(entry);
      if (typeof parsed === "object" && parsed !== null) {
        for (const k of passed) {
          if (!k) continue;
          if (Object.prototype.hasOwnProperty.call(parsed, k)) {
            let candidate = parsed[k];

            // NEW: if caller asked to unwrap nested value objects, try to extract inner .value (recursively)
            if (unwrapNestedValue) {
              // helper to walk into nested .value fields (e.g. parsed[k] -> parsed[k].value -> parsed[k].value.value ...)
              const extractInnerValue = (obj) => {
                if (obj == null) return obj;
                // if primitive, return as-is
                if (typeof obj === "string" || typeof obj === "number" || typeof obj === "boolean") return obj;
                // if object and has `value` key, go deeper
                if (typeof obj === "object" && Object.prototype.hasOwnProperty.call(obj, "value")) {
                  return extractInnerValue(obj.value);
                }
                // otherwise return the original object (will be used as label via String(...) fallback)
                return obj;
              };

              const inner = extractInnerValue(candidate);
              // if inner is primitive use it as label
              if (typeof inner === "string" || typeof inner === "number" || typeof inner === "boolean") {
                return { label: String(inner), value: entry };
              }
              // if inner is object but has common fields, prefer them (textForUrl, email, name, url)
              if (typeof inner === "object" && inner !== null) {
                const prefer = inner.textForUrl ?? inner.email ?? inner.name ?? inner.url ?? null;
                if (prefer != null) return { label: String(prefer), value: entry };
                // otherwise fall through to use candidate below
              }
            }

            // original/simple behavior: if candidate is primitive use it directly
            if (typeof candidate === "string" || typeof candidate === "number" || typeof candidate === "boolean") {
              return { label: candidate, value: entry };
            }

            // if candidate is object and has textForUrl/email/name/url, use that
            if (typeof candidate === "object" && candidate !== null) {
              const prefer = candidate.textForUrl ?? candidate.email ?? candidate.name ?? candidate.url ?? null;
              if (prefer != null) return { label: String(prefer), value: entry };
            }

            // fallback: stringify candidate (keeps previous silent behaviour)
            return { label: String(candidate), value: entry };
          }
        }
      }
    } catch (err) {
      // keep same silent behaviour as original
    }

    return { label: String(entry), value: entry };
  });
}

/*-----------------------FOR_MYSPACE-------------------------------*/

export function generateUniqueValuesMySpace(keys, opts = {}) {
  const { caseInsensitive = true } = opts;
  if (!Array.isArray(keys)) return [];

  const norm = (v) => {
    if (v === null || v === undefined) return "";
    try {
      const s = String(v).trim();
      return caseInsensitive ? s.toLowerCase() : s;
    } catch {
      return "";
    }
  };

  const seen = new Set();
  const out = [];

  const addObjIfNew = (uniqueKey, obj) => {
    const k = norm(uniqueKey);
    if (!k) return false;
    if (!seen.has(k)) {
      seen.add(k);
      out.push(obj);
      return true;
    }
    return false;
  };

  for (const item of keys) {
    if (item == null) continue;

    // Case 4: native ARRAY of objects -> flatten and use inner.value for uniqueness (push inner object)
    if (Array.isArray(item)) {
      for (const inner of item) {
        if (!inner || typeof inner !== "object") continue;
        if (inner.value !== undefined && inner.value !== null) {
          addObjIfNew(inner.value, inner);
          continue;
        }
        // fallback: if id exists, use it
        if (inner.id !== undefined && inner.id !== null) {
          addObjIfNew(inner.id, inner);
          continue;
        }
        // last resort: stringify inner
        try {
          addObjIfNew(JSON.stringify(inner), inner);
        } catch {}
      }
      continue;
    }

    // At this point item is expected to be an object (cases 1/2/3)
    if (typeof item === "object") {
      // Case 1 & 3: value is primitive -> uniqueness by item.value
      if (
        item.value !== undefined &&
        item.value !== null &&
        (typeof item.value === "string" || typeof item.value === "number" || typeof item.value === "boolean")
      ) {
        addObjIfNew(item.value, item);
        continue;
      }

      // Case 2: value is an object with textForUrl -> uniqueness by value.textForUrl
      if (item.value && typeof item.value === "object" && !Array.isArray(item.value)) {
        const v = item.value;
        if (v.textForUrl !== undefined && v.textForUrl !== null) {
          addObjIfNew(v.textForUrl, item);
          continue;
        }
        // fallback: if inner has value field
        if (
          v.value !== undefined &&
          v.value !== null &&
          (typeof v.value === "string" || typeof v.value === "number" || typeof v.value === "boolean")
        ) {
          addObjIfNew(v.value, item);
          continue;
        }
        // fallback: if inner has id
        if (v.id !== undefined && v.id !== null) {
          addObjIfNew(v.id, item);
          continue;
        }
        // last resort: stringify the value object
        try {
          addObjIfNew(JSON.stringify(v), item);
        } catch {
          continue;
        }
      }

      // If object has id but no usable value above, prefer id for uniqueness (still push the object)
      if (item.id !== undefined && item.id !== null) {
        addObjIfNew(item.id, item);
        continue;
      }

      // Final fallback: try first primitive property on the object
      const primKey = Object.keys(item).find((k) => {
        const vv = item[k];
        return typeof vv === "string" || typeof vv === "number" || typeof vv === "boolean";
      });
      if (primKey) {
        addObjIfNew(item[primKey], item);
        continue;
      }

      // Ultimate fallback: stringify item
      try {
        addObjIfNew(JSON.stringify(item), item);
      } catch {}
      continue;
    }

    // ignore any other types (shouldn't happen per your spec)
  }

  return out;
}

export function buildOptionsFromListMySpace(list = []) {
  if (!Array.isArray(list)) return [];

  const isPrimitive = (v) =>
    v !== null && v !== undefined && (typeof v === "string" || typeof v === "number" || typeof v === "boolean");

  return list.map((item) => {
    try {
      // 1️⃣ Case: object with primitive "value"
      if (item && typeof item === "object" && isPrimitive(item.value)) {
        return { label: String(item.value), value: item };
      }

      // 2️⃣ Case: object with nested { url, textForUrl } under "value"
      if (item && typeof item.value === "object" && !Array.isArray(item.value)) {
        const inner = item.value;
        if (isPrimitive(inner.textForUrl)) {
          return { label: String(inner.textForUrl), value: item };
        }
        if (isPrimitive(inner.value)) {
          return { label: String(inner.value), value: item };
        }
        if (isPrimitive(inner.url)) {
          return { label: String(inner.url), value: item };
        }
      }

      // 3️⃣ Case: direct choice object (id, value, color_code)
      if (item && typeof item === "object") {
        if (isPrimitive(item.value)) {
          return { label: String(item.value), value: item };
        }
        if (isPrimitive(item.label)) {
          return { label: String(item.label), value: item };
        }
        if (isPrimitive(item.textForUrl)) {
          return { label: String(item.textForUrl), value: item };
        }
      }

      // 4️⃣ Fallback: stringify object for label
      try {
        return { label: JSON.stringify(item), value: item };
      } catch {
        return { label: String(item), value: item };
      }
    } catch {
      return { label: String(item), value: item };
    }
  });
}

//for customdashboard options

/*-----------------------FOR_CUSTOM_DASHBOARD-------------------------------*/

export function generateUniqueValuesForCustomDashboard(keys, opts = {}) {
  const { caseInsensitive = true } = opts;
  if (!Array.isArray(keys)) return [];

  const norm = (v) => {
    if (v === null || v === undefined) return "";
    try {
      const s = String(v).trim();
      return caseInsensitive ? s.toLowerCase() : s;
    } catch {
      return "";
    }
  };

  const seen = new Set();
  const out = [];

  const addIfNew = (keyRaw, obj) => {
    const k = norm(keyRaw);
    if (!k) return false;
    if (!seen.has(k)) {
      seen.add(k);
      out.push(obj);
      return true;
    }
    return false;
  };

  for (const item of keys) {
    if (!item || typeof item !== "object") continue;

    const val = item.value;

    // Case 4: value is an array of choice objects -> flatten and push inner objects (unique by inner.value)
    if (Array.isArray(val)) {
      for (const inner of val) {
        if (!inner || typeof inner !== "object") continue;
        // prefer inner.value for uniqueness
        if (inner.value !== undefined && inner.value !== null) {
          addIfNew(inner.value, inner);
          continue;
        }
        // fallback to inner.id
        if (inner.id !== undefined && inner.id !== null) {
          addIfNew(inner.id, inner);
          continue;
        }
        // final fallback: stringify inner
        try {
          addIfNew(JSON.stringify(inner), inner);
        } catch {}
      }
      continue;
    }

    // If value is primitive (case 1)
    if (val !== undefined && (typeof val === "string" || typeof val === "number" || typeof val === "boolean")) {
      addIfNew(val, item); // push the wrapper/original item
      continue;
    }

    // If value is an object (case 2 or 3)
    if (val && typeof val === "object") {
      // case 2: prefer textForUrl if present
      if (val.textForUrl !== undefined && val.textForUrl !== null) {
        addIfNew(val.textForUrl, item);
        continue;
      }
      // case 3: prefer value.value
      if (
        val.value !== undefined &&
        val.value !== null &&
        (typeof val.value === "string" || typeof val.value === "number" || typeof val.value === "boolean")
      ) {
        addIfNew(val.value, item);
        continue;
      }
      // fallback: inner id
      if (val.id !== undefined && val.id !== null) {
        addIfNew(val.id, item);
        continue;
      }
      // fallback stringify val
      try {
        addIfNew(JSON.stringify(val), item);
      } catch {}
      continue;
    }

    // fallback: if item has id use it
    if (item.id !== undefined && item.id !== null) {
      addIfNew(item.id, item);
      continue;
    }

    // fallback: try first primitive prop on item
    const prim = Object.keys(item).find((k) => {
      const vv = item[k];
      return typeof vv === "string" || typeof vv === "number" || typeof vv === "boolean";
    });
    if (prim) {
      addIfNew(item[prim], item);
      continue;
    }

    // ultimate fallback: stringify whole item
    try {
      addIfNew(JSON.stringify(item), item);
    } catch {}
  }

  return out;
}

export function buildOptionsFromListForCustomDashboard(list = []) {
  if (!Array.isArray(list)) return [];

  const safeParse = (s) => {
    if (typeof s !== "string") return null;
    try {
      return JSON.parse(s);
    } catch {
      return null;
    }
  };

  const isPrimitive = (v) => (v === null ? false : ["string", "number", "boolean"].includes(typeof v));

  const shortJson = (o) => {
    try {
      const s = JSON.stringify(o);
      return s.length > 80 ? s.slice(0, 77) + "..." : s;
    } catch {
      return String(o);
    }
  };

  return list.map((item) => {
    try {
      // defensive: if not an object, coerce
      if (!item || typeof item !== "object") {
        return { label: String(item), value: item };
      }

      const val = item.value;

      // 1) item.value is primitive
      if (isPrimitive(val)) {
        return { label: String(val), value: item };
      }

      // 2) item.value is object -> prefer textForUrl, then value, then url
      if (val && typeof val === "object" && !Array.isArray(val)) {
        if (isPrimitive(val.textForUrl)) return { label: String(val.textForUrl), value: item };
        if (isPrimitive(val.value)) return { label: String(val.value), value: item };
        if (isPrimitive(val.url)) return { label: String(val.url), value: item };
      }

      // 3) direct fields on item
      if (isPrimitive(item.label)) return { label: String(item.label), value: item };
      if (isPrimitive(item.email)) return { label: String(item.email), value: item };
      if (isPrimitive(item.name)) return { label: String(item.name), value: item };

      // 4) item.value might be a stringified JSON -> try parse for inner textForUrl/value/url
      if (typeof val === "string") {
        const parsed = safeParse(val);
        if (parsed && typeof parsed === "object") {
          if (isPrimitive(parsed.textForUrl)) return { label: String(parsed.textForUrl), value: item };
          if (isPrimitive(parsed.value)) return { label: String(parsed.value), value: item };
          if (isPrimitive(parsed.url)) return { label: String(parsed.url), value: item };
        }
      }

      // 5) id fallback
      if (isPrimitive(item.id)) return { label: String(item.id), value: item };

      // 6) first primitive prop on item
      for (const k of Object.keys(item)) {
        const v = item[k];
        if (isPrimitive(v)) return { label: String(v), value: item };
      }

      // 7) ultimate fallback: short JSON preview
      return { label: shortJson(item), value: item };
    } catch (err) {
      // silent fallback
      return { label: String(item), value: item };
    }
  });
}

// utils/getFlattenedUniqueValues.js
// assumes safeJsonParse is available

// utils/getFlattenedUniqueValues.js
// assumes safeJsonParse is available

export function getFlattenedUniqueValues(column, limit = 5000) {
  const sortedUniqueValues = [
    ...new Set(
      Array.from(column.getFacetedUniqueValues().keys()).flatMap((item) => {
        try {
          const parsed = safeJsonParse(item);
          return Array.isArray(parsed) ? parsed.map((opt) => opt?.value).filter(Boolean) : [];
        } catch (err) {
          return [];
        }
      })
    ),
  ]
    .sort()
    .slice(0, 5000);

  let keys = Array.from(column.getFacetedUniqueValues().keys()).filter((key) => key !== null);
  return keys.sort().slice(0, 5000);
}

export function findFirstKey(parsed, keys) {
  if (parsed == null || typeof parsed !== "object") return undefined;
  for (const k of keys) {
    if (k && Object.prototype.hasOwnProperty.call(parsed, k)) {
      return parsed[k];
    }
  }
  return undefined;
}
